import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ApprovalDashboardFilters } from "../types.ts";
import { API_APPROVAL_DASHBOARD_EMPLOYEES } from "../../../../../config/Endpoints.js";
import { showError } from "../../../../utils/toast.ts";
import type {
  ApprovalEmployeeRow,
  ApprovalStatusFilter,
} from "../types.ts";

export const INNER_PAGE_SIZE = 10;

interface RawEmployeesResponse {
  content?: ApprovalEmployeeRow[];
  totalElements?: number;
}

function employeeRowKey(row: ApprovalEmployeeRow): string {
  const project = (row.projectName ?? "").trim();
  return `${row.timesheetId ?? ""}-${row.employeeId}-${row.timesheetDate}-${project}`;
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    if (typeof msg === "string" && msg.trim() !== "") return msg;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function useApproverEmployees(
  approverId: number,
  status: ApprovalStatusFilter,
  initialEmployees: ApprovalEmployeeRow[],
  totalEmployees: number,
  filters: ApprovalDashboardFilters,
) {
  const [lastFetchedPage, setLastFetchedPage] = useState(-1);
  const [employees, setEmployees] =
    useState<ApprovalEmployeeRow[]>(initialEmployees);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(
    initialEmployees.length < totalEmployees,
  );
  const token = Cookies.get("accessToken");

  useEffect(() => {
    setLastFetchedPage(-1);
    setEmployees(initialEmployees);
    setHasMore(initialEmployees.length < totalEmployees);
  }, [approverId, status, totalEmployees]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    const fetchPage = lastFetchedPage + 1;
    setLoading(true);

    try {
    const params: Record<string, string | number> = {
  page: fetchPage,
  size: INNER_PAGE_SIZE,
    status,
    approverStatus: "false",
};

// if (status !== "ALL") {
//   params.status = status;
// }

if (filters.employeeId != null) {
  params.employeeId = filters.employeeId;
}

if (filters.approverId != null) {
  params.approverId = filters.approverId;
}

if (filters.region) {
  params.region = filters.region;
}

if (filters.fromDate) {
  params.fromDate = filters.fromDate;
}

if (filters.toDate) {
  params.toDate = filters.toDate;
}

      const res = await axios.get<RawEmployeesResponse>(
        API_APPROVAL_DASHBOARD_EMPLOYEES(approverId),
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const body = res.data ?? {};
      const nextItems = Array.isArray(body.content) ? body.content : [];

      setEmployees((current) => {
        const seen = new Set(current.map(employeeRowKey));
        const uniqueNext = nextItems.filter(
          (row) => !seen.has(employeeRowKey(row)),
        );
        const merged = [...current, ...uniqueNext];
        const total =
          typeof body.totalElements === "number"
            ? body.totalElements
            : totalEmployees;
        setHasMore(merged.length < total && uniqueNext.length > 0);
        return merged;
      });
      setLastFetchedPage(fetchPage);
    } catch (err) {
      const msg = getApiErrorMessage(
        err,
        "Failed to load employees for this approver.",
      );
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [
    approverId,
    hasMore,
    lastFetchedPage,
    loading,
    status,
    token,
    totalEmployees,
  ]);

  return {
    employees,
    loading,
    hasMore,
    loadMore,
  };
}
